import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Sparkles, Copy, Heart, Info } from 'lucide-react'
import { generateElfNames, type ElfRace, type ElfGender, type ElfStyle, type ElfName } from '@/client/lib/elves'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

interface ElfGeneratorProps {
  lockedRace?: ElfRace
}

export function ElfGenerator({ lockedRace }: ElfGeneratorProps) {
  const [gender, setGender] = useState<ElfGender>('Male')
  const [race, setRace] = useState<ElfRace>(lockedRace || 'High Elf')
  const [style, setStyle] = useState<ElfStyle>('Classic')
  const [count, setCount] = useState(10)
  const [names, setNames] = useState<ElfName[]>([])
  const [selectedName, setSelectedName] = useState<ElfName | null>(null)

  const handleGenerate = () => {
    const generated = generateElfNames(race, gender, style, count)
    setNames(generated)
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    toast.success(`Copied "${text}" to clipboard`)
  }

  return (
    <div className="w-full max-w-4xl mx-auto space-y-8">
      <Card className="glass-panel border-white/10 overflow-hidden">
        <CardContent className="p-6 md:p-10 space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Left Column: Controls */}
            <div className="space-y-6">
              <div className="space-y-3">
                <Label className="text-sm font-medium opacity-70">Gender</Label>
                <RadioGroup 
                  defaultValue={gender} 
                  onValueChange={(v) => setGender(v as ElfGender)}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Male" id="male" />
                    <Label htmlFor="male">Male</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Female" id="female" />
                    <Label htmlFor="female">Female</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Neutral" id="neutral" />
                    <Label htmlFor="neutral">Neutral</Label>
                  </div>
                </RadioGroup>
              </div>

              {!lockedRace && (
                <div className="space-y-3">
                  <Label className="text-sm font-medium opacity-70">Race</Label>
                  <Select value={race} onValueChange={(v) => setRace(v as ElfRace)}>
                    <SelectTrigger className="bg-background/50">
                      <SelectValue placeholder="Select Race" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="High Elf">High Elf</SelectItem>
                      <SelectItem value="Dark Elf">Dark Elf</SelectItem>
                      <SelectItem value="Wood Elf">Wood Elf</SelectItem>
                      <SelectItem value="Christmas Elf">Christmas Elf</SelectItem>
                      <SelectItem value="Half-Elf">Half-Elf</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-3">
                <Label className="text-sm font-medium opacity-70">Style</Label>
                <div className="flex flex-wrap gap-2">
                  {(['Classic', 'Exotic', 'Ancient'] as ElfStyle[]).map((s) => (
                    <Button 
                      key={s}
                      variant={style === s ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setStyle(s)}
                      className="flex-1"
                    >
                      {s}
                    </Button>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column: Count & Action */}
            <div className="space-y-6 flex flex-col justify-between">
              <div className="space-y-3">
                <Label className="text-sm font-medium opacity-70">Count</Label>
                <div className="grid grid-cols-4 gap-2">
                  {[1, 5, 10, 50].map((c) => (
                    <Button 
                      key={c}
                      variant={count === c ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setCount(c)}
                      className="relative overflow-hidden"
                    >
                      {c} {c === 50 && <span className="text-[8px] absolute top-0 right-0 bg-yellow-500 text-black px-1 scale-75">PRO</span>}
                    </Button>
                  ))}
                </div>
              </div>

              <Button 
                onClick={handleGenerate} 
                size="lg" 
                className="w-full h-16 text-lg font-bold shadow-lg transition-all hover:scale-[1.02] active:scale-[0.98] bg-primary hover:bg-primary/90"
              >
                <Sparkles className="mr-2 h-6 w-6" />
                Generate Names
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results Section */}
      {names.length > 0 && (
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {names.map((n, idx) => (
              <Card key={idx} className="group hover:border-primary/50 transition-colors cursor-pointer bg-card/50">
                <CardContent className="p-4 flex flex-col gap-2">
                  <div className="flex justify-between items-start">
                    <span className="text-sm opacity-50 font-serif italic text-primary">🌿 {race}</span>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => copyToClipboard(n.name)}>
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setSelectedName(n)}>
                        <Info className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold tracking-tight">{n.name} {n.surname}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-1">"{n.meaning}"</p>
                  <div className="flex gap-2 mt-2">
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => copyToClipboard(`${n.name} ${n.surname}`)}>Copy</Button>
                    <Button variant="ghost" size="sm" onClick={() => toast.success("Saved to favorites")}><Heart className="h-4 w-4" /></Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Detail Modal */}
      <Dialog open={!!selectedName} onOpenChange={() => setSelectedName(null)}>
        <DialogContent className="sm:max-w-md">
          {selectedName && (
            <>
              <DialogHeader>
                <DialogTitle className="text-3xl font-bold">{selectedName.name} {selectedName.surname}</DialogTitle>
                <DialogDescription className="text-primary font-medium italic">
                  "{selectedName.meaning}"
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Pronunciation</h4>
                  <p className="text-lg font-serif">/{selectedName.pronunciation}/</p>
                </div>
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Lore & Background</h4>
                  <p className="text-sm leading-relaxed">{selectedName.background}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button className="flex-1" onClick={() => copyToClipboard(`${selectedName.name} ${selectedName.surname}`)}>Copy Full Name</Button>
                <Button variant="outline" onClick={() => setSelectedName(null)}>Close</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
